import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Tooltip, Snackbar } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import MusicIcon from '@mui/icons-material/MusicNoteOutlined';
import AudioIcon from '@mui/icons-material/HeadphonesOutlined';
import ShareIcon from '@mui/icons-material/IosShareOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import LightIcon from '@mui/icons-material/LightModeOutlined';
import DarkIcon from '@mui/icons-material/DarkModeOutlined';

import { colorRayo, colorIdentidad, getRayo } from '../config/rayos';
import { formatoTitulo } from '../config/titulos';
import { resolver } from '../config/personalizacion';
import { useLector, planLabelKey } from '../config/lector';
import { usePiece, usePreviewPiece, useCatalog } from '../content/hooks';
import { THIRD_PARTY_TYPE } from '../content/contentApi';
import { pieceBlocks, resolveBlocks } from '../content/blocks';
import PieceBlocks, { InlineRuns } from '../components/PieceBlocks';
import { useModo } from '../config/modo';
import { useTranslation } from 'react-i18next';
import { nombreVoz, textoVoz } from '../config/nombres_voz';
import AvisoIdioma from '../components/AvisoIdioma';
import LuzDeLaVoz from '../components/LuzDeLaVoz';
import ReproductorAudio, { ETIQUETAS_AUDIO, ETIQUETAS_MUSICA } from '../components/ReproductorAudio';
import './canalizacion.css';

const CanalizacionPage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();

  /* ⬅️ VOLVER DONDE ESTABA, no a la biblioteca en blanco (Faina, 1-sep).
     La biblioteca manda su dirección exacta al abrir una pieza —búsqueda,
     filtros, página—. Si se entra por un enlace directo o recargando no hay
     `state`, y entonces sí se vuelve a la biblioteca entera, que es lo correcto:
     no había ningún paso anterior que conservar. */
  const location = useLocation();
  const { lector, plan, identificado } = useLector();

  /* 🔴 DEF 73 (Faina, 29-ago): sin registro solo se abre el mensaje del dia.
     «Cuando pinche alguna entidad, solo le debe mostrar los cuadros de las
     canalizaciones de la biblioteca, pero no los va poder abrir hasta que se
     suscriba. Una persona que no esta suscrita solo podra tener acceso al
     mensaje del dia.»
     La ficha NO se cierra con un portazo: se abre, enseña de quien es y de que
     va, y explica que falta. Un clic que no hace nada deja al lector sin saber
     por que; una pagina que lo dice, invita. */
  /* ⭐ EL MENSAJE DEL DÍA SE ABRE ENTERO, PARA CUALQUIERA (Faina, 1-sep):
     «en el mensaje del día, cuando lo pinche podrá tener todo disponible:
     compartir, imprimir, música y escuchar».

     No es solo que se pueda LEER sin cuenta —eso ya lo decía la Def 73—: es que
     ese día la pieza viene con TODO lo que el sitio sabe hacer con una
     canalización. Una muestra a medias no enseña lo que hay; enseña un muro.
     Por eso `delDia` abre también las herramientas, no solo el texto. */
  const { modo, esOscuro, alternar, alineacion, tam, agrandar, achicar } = useModo();

  const [avance, setAvance] = useState(0);
  const [aviso, setAviso] = useState('');
  const [musicaAbierta, setMusicaAbierta] = useState(false);
  const [audioAbierto, setAudioAbierto] = useState(false);
  useEffect(() => {
    setMusicaAbierta(false);
    setAudioAbierto(false);
  }, [slug]);
  const cuerpoRef = useRef(null);

  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview') || '';
  const isPreview = Boolean(previewToken);

  const { piece: livePiece, loading: cargandoLive } = usePiece(isPreview ? null : slug);
  const { piece: previewPiece, loading: cargandoPreview } = usePreviewPiece(previewToken);
  const { items: piezasCatalogo } = useCatalog();
  const piece = isPreview ? previewPiece : livePiece;
  const cargandoPieza = isPreview ? cargandoPreview : cargandoLive;
  const propia = piece && (isPreview || piece.slug === slug) ? piece : null;

  useEffect(() => {
    if (!isPreview) return undefined;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, [isPreview]);

  const pieza = propia;
  const delDia = Boolean(pieza && pieza.esDelDia);
  const puedeLeer = Boolean(pieza) && !pieza.bloqueada;

  /* ⬅️ VOLVER DONDE ESTABA, no a un catálogo en blanco (Faina, 1-sep). La lista
     manda su dirección exacta al abrir una pieza —búsqueda, filtros, página—.
     Si se entra por un enlace directo o recargando no hay `state`, y entonces
     se vuelve al catálogo entero, que es lo correcto: no había paso anterior. */
  const volverA = (location.state && location.state.volverA) || '/canalizaciones';
  const catalogo = '/canalizaciones';

  // Cuantas canalizaciones tiene esta voz, para el bloque del final.
  const deLaVoz = useMemo(
    () => (pieza ? piezasCatalogo.filter((c) => c.entidad === pieza.entidad) : []),
    [pieza, piezasCatalogo]
  );

  /* Tres para seguir leyendo: primero de la misma voz, despues las que
     comparten ensenanza, y al final se completa con las mas cercanas. */
  const sugeridas = useMemo(() => {
    if (!pieza) return [];
    const otras = piezasCatalogo.filter((c) => c.slug !== pieza.slug);
    const puntaje = (c) => {
      let p = 0;
      if (c.entidad === pieza.entidad) p += 2;
      p += c.ensenanzas.filter((e) => (pieza.ensenanzas || []).includes(e)).length;
      return p;
    };
    return [...otras]
      .map((c) => ({ c, p: puntaje(c) }))
      .sort((a, b) => b.p - a.p)
      .slice(0, 3)
      .map((x) => x.c);
  }, [pieza, piezasCatalogo]);

  const rayo = pieza ? colorRayo(pieza.entidad, modo) : 'var(--acento)';        // texto
  const luz  = pieza ? colorIdentidad(pieza.entidad, modo) : 'var(--acento)';   // grafismos

  // El texto, ya resuelto segun el plan del lector.
  const blocks = useMemo(() => {
    if (!pieza) return [];
    // Un saludo que es solo vocativo y nombre («{Querido amigo/Querida amiga}
    // {NOMBRE}:») queda vacio sin nombre: ese bloque no se pinta.
    return resolveBlocks(pieceBlocks(pieza), (text) => resolver(text, lector, pieza.entidad, plan.personaliza));
  }, [pieza, lector, plan.personaliza]);

  const firma = useMemo(
    () => (pieza ? resolver(pieza.firma, lector, pieza.entidad, plan.personaliza) : ''),
    [pieza, lector, plan.personaliza]
  );

  const pedirCuenta = Boolean(pieza) && !identificado && (!pieza.requiredPlan || pieza.requiredPlan === 'free');
  const requiredPlanKey = pieza && pieza.requiredPlan ? planLabelKey(pieza.requiredPlan) : null;

  const nombreVisible = plan.personaliza ? (lector.nombre || '').trim() : '';


  useEffect(() => { window.scrollTo(0, 0); }, [slug]);



  // Barra de avance de lectura
  useEffect(() => {
    const alDesplazar = () => {
      const el = cuerpoRef.current;
      if (!el) return;
      const inicio = el.offsetTop;
      const alto = el.offsetHeight - window.innerHeight * 0.6;
      const y = window.scrollY - inicio;
      setAvance(Math.min(100, Math.max(0, (y / Math.max(alto, 1)) * 100)));
    };
    window.addEventListener('scroll', alDesplazar, { passive: true });
    alDesplazar();
    return () => window.removeEventListener('scroll', alDesplazar);
  }, [slug]);

  if (!pieza) {
    if (cargandoPieza) {
      return (
        <Container sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'var(--texto-tenue)' }}>{t('ficha.abriendo')}</Typography>
        </Container>
      );
    }
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3 }}>{isPreview ? t('ficha.preview_missing') : t('ficha.no_esta')}</Typography>
        <Button variant="outlined" onClick={() => navigate(volverA)}>{t('ficha.ver_biblioteca')}</Button>
      </Container>
    );
  }

  const compartir = async () => {
    const datos = {
      title: pieza.titulo,
      text: `${pieza.titulo} — ${pieza.entidad}`,
      url: window.location.href
    };
    try {
      if (navigator.share) { await navigator.share(datos); return; }
      await navigator.clipboard.writeText(window.location.href);
      setAviso(t('ficha.enlace_copiado'));
    } catch (e) {
      setAviso(t('ficha.no_compartir'));
    }
  };

  const alBloqueado = (queFalta) => setAviso(queFalta);

  const todasLasAcciones = [
    {
      clave: 'compartir', icono: <ShareIcon fontSize="small" />, texto: t('ficha.compartir'),
      libre: true, alPulsar: compartir,
      ayuda: t('ficha.compartir_ayuda')
    },
    {
      clave: 'imprimir', icono: <PrintIcon fontSize="small" />, texto: t('ficha.imprimir'),
      libre: delDia || plan.imprimir, alPulsar: () => window.print(),
      ayuda: plan.imprimir
        ? t('ficha.imprimir_ayuda')
        : t('ficha.imprimir_plan2')
    },
    {
      clave: 'musica', icono: <MusicIcon fontSize="small" />, texto: t('ficha.musica'),
      libre: delDia || plan.musica,
      alPulsar: () => {
        const cancion = pieza.musica;
        if (cancion && cancion.audioUrl) {
          setMusicaAbierta((abierta) => !abierta);
          return;
        }
        setAviso(cancion && cancion.locked ? t('ficha.musica_plan2') : t('ficha.sin_musica'));
      },
      ayuda: plan.musica
        ? t('ficha.musica_ayuda')
        : t('ficha.musica_plan2')
    },
    {
      /* El audio tiene dos versiones (Faina, 26-ago): la GENERICA, en primera
         persona y sin nombre, que oye cualquiera y ademas se publica en Spotify
         y YouTube; y la PERSONALIZADA, que dice el nombre del lector y se abre
         con el plan 3. */
      clave: 'audio', icono: <AudioIcon fontSize="small" />, texto: t('ficha.escuchar'),
      libre: delDia || plan.audioGenerico,
      alPulsar: () => {
        if (pieza.audio && pieza.audio.audioUrl) {
          setAudioAbierto((abierto) => !abierto);
          return;
        }
        setAviso(pieza.audio && pieza.audio.locked ? t('ficha.audio_plan') : t('ficha.sin_audio'));
      },
      ayuda: plan.audioPersonal
        ? t('ficha.audio_ayuda_nombre')
        : t('ficha.audio_ayuda')
    }
  ];
  const esDeTerceros = pieza.tipoContenido === THIRD_PARTY_TYPE;
  const accionDisponible = (accion) => {
    if (isPreview && (accion.clave === 'compartir' || accion.clave === 'imprimir')) return false;
    if (esDeTerceros && accion.clave === 'compartir') return false;
    if (esDeTerceros && accion.clave === 'musica') return Boolean(pieza.musica && pieza.musica.audioUrl);
    if (esDeTerceros && accion.clave === 'audio') return Boolean(pieza.audio && pieza.audio.audioUrl);
    return true;
  };
  const acciones = todasLasAcciones.filter(accionDisponible);

  return (
    <>
      {isPreview && (
        <>
          <Box role="status" className="no-imprimir" sx={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1350,
            bgcolor: 'var(--acento)', color: '#000', textAlign: 'center',
            fontFamily: 'var(--fuente-titulo)', fontSize: '.8rem', fontWeight: 500,
            letterSpacing: '.04em', py: .8, px: 2
          }}>
            {t('ficha.preview_banner')}
          </Box>
          <Box className="no-imprimir" aria-hidden="true" sx={{ height: '2.2rem' }} />
        </>
      )}
      <Box className="no-imprimir" sx={{ position: 'sticky', top: 0, zIndex: 5, height: '2px', bgcolor: 'var(--linea)' }}>
        <Box sx={{ height: '100%', width: `${avance}%`, bgcolor: luz, transition: 'width .12s linear' }} />
      </Box>

      <LuzDeLaVoz entidad={pieza.entidad} modo={modo} pieza={pieza} />

      <Container component="article" sx={{ maxWidth: '46rem !important', pb: 8 }}>

        <Box className="no-imprimir" sx={{ pb: 1, mt: -1 }}>
          <Button
            component={RouterLink}
            to={volverA}
            startIcon={<ArrowBackIcon sx={{ fontSize: '.8rem' }} />}
            sx={{ color: 'var(--texto-tenue)', fontSize: '.75rem', px: 0, '&:hover': { color: 'var(--texto)', bgcolor: 'transparent' } }}
          >
            {t('biblio.titulo')}
          </Button>
        </Box>

        <Typography component="h1" sx={{
          fontFamily: 'var(--fuente-lectura)', fontWeight: 300,
          fontSize: 'clamp(2rem, 5.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em',
          textAlign: 'center', mb: 2
        }}>
          {formatoTitulo(pieza.titulo)}
        </Typography>

        {/* Ficha corta */}
        <Typography component="p" className="ficha-corta" sx={{
          textAlign: 'center', fontFamily: 'var(--fuente-titulo)', fontSize: '.82rem',
          color: 'var(--texto-tenue)', letterSpacing: '.04em', mb: 5
        }}>
          {t('ficha.canalizado_por')}{' '}
          <Box component="span"
               sx={{ color: 'var(--acento)', fontWeight: 500 }}>
            {pieza.canalizador}
          </Box>
          {esDeTerceros && (
            <Box component="span" sx={{ display: 'block', mt: .5 }}>{t('ficha.de_terceros')}</Box>
          )}
        </Typography>

        {/* SIN UMBRAL AQUI (Faina, 26-ago): «solo dejalo para las canalizaciones
            del dia en el home; sacalo del resto». El silencio se ofrece una vez,
            al llegar — repetido en cada pieza dejaba de ser un rito y pasaba a
            ser un peaje. Vive en la franja del mensaje del dia. */}

        {/* Sin registro: el primer parrafo y la puerta. Se enseña el principio
            —para que se vea que hay algo de verdad detras, no un muro seco— y
            debajo, lo que falta para seguir. */}
        <AvisoIdioma />
        {!puedeLeer && (
          <Box className="cerrada">
            <Typography className={`cuerpo-canalizacion alin-${alineacion} parrafo primero cerrada-asomo`}
                        sx={{ '--rayo': rayo, '--tam': `${tam}rem` }}>
              {blocks[0] && <InlineRuns runs={blocks[0].items[0]} />}
            </Typography>

            {/* 🔴 DIRECTO (Faina, 29-ago): «este mensaje debe ser mucho mas
                directo». Antes explicaba por que —«cada canalizacion esta
                escrita para una sola persona»— y esa razon es buena, pero no en
                el momento en que alguien quiere leer: ahi solo hace falta saber
                QUE hay que hacer y QUE se consigue. La frase es suya. */}
            <Box className="cerrada-aviso" sx={{ '--rayo': rayo }}>
              {/* Mismo formato que la caja del home, que es lo acordado: el
                  boton arriba y debajo lo que se consigue. */}
              <span className="cerrada-luz" aria-hidden="true" />
              {/* 🟢 DEF 101 (Faina, 09-10-2026): registrarse ya no abre las
                  canalizaciones —el plan gratuito es el mensaje del día—, así
                  que la puerta lleva a los planes. */}
              <RouterLink to={pedirCuenta ? '/entrar' : '/#planes'} className="cerrada-boton">
                {pedirCuenta ? t('entrar.crear_tu_cuenta') : t('cuenta.ver_planes')}
              </RouterLink>
              <Typography className="cerrada-titulo">
                {pedirCuenta
                  ? t('ficha.requiere_cuenta')
                  : (requiredPlanKey ? t('ficha.requiere_plan', { plan: t(requiredPlanKey) }) : t('ficha.en_los_planes'))}
              </Typography>
              {!identificado && (
                <Typography className="cerrada-pie">
                  {t('ficha.ya_tienes_cuenta')} <RouterLink to="/entrar">{t('menu.entrar')}</RouterLink>
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* El texto */}
        {puedeLeer && (<>
        <Box ref={cuerpoRef} className={`cuerpo-canalizacion alin-${alineacion}`} sx={{ '--rayo': rayo, '--tam': `${tam}rem` }}>
          <PieceBlocks blocks={blocks} name={nombreVisible} nameColor={rayo} />

          {firma && (
            <Typography component="p" className="firma" sx={{ color: rayo, borderTopColor: 'var(--linea)' }}>
              {firma}
            </Typography>
          )}
        </Box>
        </>)}

        {/* Los iconos */}
        <Box className="no-imprimir" sx={{
          mt: 4, pt: 3, borderTop: '1px solid var(--linea)',
          display: 'flex', flexWrap: 'wrap', gap: 1
        }}>
          {acciones.map((a) => (
            <Tooltip key={a.clave} title={a.ayuda} arrow>
              {/* El span deja ver el aviso también sobre el botón apagado: un
                  botón deshabilitado no recibe el ratón. */}
              <span style={{ display: 'inline-flex' }}>
              <Box
                component="button"
                type="button"
                onClick={a.libre ? a.alPulsar : () => alBloqueado(a.ayuda)}
                aria-label={a.texto}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: .7, cursor: 'pointer',
                  fontFamily: 'var(--fuente-titulo)', fontSize: '.76rem', letterSpacing: '.1em',
                  textTransform: 'uppercase', borderRadius: '2px', px: 1.4, py: .9,
                  border: '1px solid var(--linea)', bgcolor: 'transparent',
                  color: a.libre ? 'var(--texto-suave)' : 'var(--texto-tenue)',
                  opacity: a.libre ? 1 : .6,
                  '&:hover': { borderColor: a.libre ? rayo : 'var(--linea)', color: a.libre ? 'var(--texto)' : 'var(--texto-tenue)' }
                }}
              >
                {a.icono}{a.texto}
                {!a.libre && <LockIcon sx={{ fontSize: '.8rem', ml: .3 }} />}
              </Box>
              </span>
            </Tooltip>
          ))}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={t('ficha.letra_chica')} arrow>
            <Box component="button" type="button" aria-label={t('ficha.letra_chica')}
              onClick={achicar}
              sx={btnIcono}><TextDecreaseIcon fontSize="small" /></Box>
          </Tooltip>
          <Tooltip title={t('ficha.letra_grande')} arrow>
            <Box component="button" type="button" aria-label={t('ficha.letra_grande')}
              onClick={agrandar}
              sx={btnIcono}><TextIncreaseIcon fontSize="small" /></Box>
          </Tooltip>
          <Tooltip title={esOscuro ? t('ficha.fondo_claro') : t('ficha.fondo_oscuro')} arrow>
            <Box component="button" type="button" aria-label={t('ficha.cambiar_fondo')}
              onClick={alternar} sx={btnIcono}>
              {esOscuro ? <LightIcon fontSize="small" /> : <DarkIcon fontSize="small" />}
            </Box>
          </Tooltip>
        </Box>

        {musicaAbierta && pieza.musica && pieza.musica.audioUrl && (
          <ReproductorAudio
            key={pieza.musica.audioUrl}
            titulo={pieza.musica.title}
            artista={pieza.musica.artist}
            url={pieza.musica.audioUrl}
            color={rayo}
            etiquetas={ETIQUETAS_MUSICA}
          />
        )}

        {audioAbierto && pieza.audio && pieza.audio.audioUrl && (
          <ReproductorAudio
            key={pieza.audio.audioUrl}
            titulo={formatoTitulo(pieza.titulo)}
            url={pieza.audio.audioUrl}
            color={rayo}
            etiquetas={ETIQUETAS_AUDIO}
          />
        )}

        {/* Procedencia: solo en la hoja impresa. En pantalla no le dice nada al lector,
            pero en papel permite saber de que pieza salio la hoja. */}
        <Box className="procedencia solo-imprimir">
          {pieza.id} · {formatoTitulo(pieza.titulo)} · {nombreVoz(pieza.entidad, t)}, {t('ficha.canalizado_por_min')} {pieza.canalizador} · jeshua.ong
        </Box>

        {/* NO HAY INVITACION A REGISTRARSE AQUI (Faina, 26-ago): «saca de todas las
            canalizaciones 'esta canalizacion es para ti'; el registrate debe estar
            en el home». La pieza se lee entera y se acaba en su firma, sin que el
            sitio se meta a vender en medio de lo que la entidad vino a decir.
            La invitacion vive en el home, al cerrar. */}

        {/* La voz que hablo */}
        <Box className="no-imprimir" component={RouterLink} to={`${catalogo}?voz=${encodeURIComponent(pieza.entidad)}`}
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, mt: 6, p: 2.5, textDecoration: 'none',
            border: '1px solid var(--linea)',
            '&:hover': { bgcolor: 'var(--superficie)', borderColor: rayo }
          }}
        >
          <Box aria-hidden="true" sx={{
            width: '3rem', height: '3rem', borderRadius: '50%', flex: 'none',
            background: `radial-gradient(circle at 50% 35%, ${luz} 0%, ${luz}33 55%, transparent 72%)`
          }} />
          <Box>
            <Typography component="span" sx={{
              display: 'block', fontFamily: 'var(--fuente-titulo)', fontSize: '.7rem',
              letterSpacing: '.16em', textTransform: 'uppercase', color: rayo, mb: .3
            }}>
              {t('ficha.rayo')} {textoVoz('rayo.', pieza.entidad, t, getRayo(pieza.entidad).nombre)}
            </Typography>
            <Typography component="span" sx={{ display: 'block', color: 'var(--texto)', fontSize: '1.1rem' }}>
              {t('ficha.todo_lo_que_dice', { voz: nombreVoz(pieza.entidad, t) })}
            </Typography>
            <Typography component="span" sx={{ display: 'block', color: 'var(--texto-tenue)', fontSize: '.85rem', fontFamily: 'var(--fuente-titulo)' }}>
              {deLaVoz.length} {deLaVoz.length === 1 ? t('filtros.una') : t('filtros.varias')}
            </Typography>
          </Box>
        </Box>

        {/* Seguir leyendo */}
        {sugeridas.length > 0 && (
          <Box className="no-imprimir" sx={{ mt: 5 }}>
            <Typography component="h2" sx={{
              fontFamily: 'var(--fuente-titulo)', fontSize: '.72rem', letterSpacing: '.18em',
              textTransform: 'uppercase', color: 'var(--texto-tenue)', mb: 1.5
            }}>
              {t('ficha.seguir_leyendo')}
            </Typography>

            <Box sx={{ display: 'grid', gap: '1px', bgcolor: 'var(--linea)', border: '1px solid var(--linea)' }}>
              {sugeridas.map((c) => {
                const suRayo = colorRayo(c.entidad, modo);
                return (
                  <Box
                    key={c.slug}
                    component={RouterLink}
                    to={`/canalizacion/${c.slug}`}
                    sx={{
                      display: 'block', p: 2.2, textDecoration: 'none', bgcolor: 'var(--fondo)',
                      borderLeft: `3px solid ${suRayo}`,
                      '&:hover': { bgcolor: 'var(--superficie)' }
                    }}
                  >
                    <Typography component="span" sx={{
                      display: 'block', fontFamily: 'var(--fuente-titulo)', fontSize: '.68rem',
                      letterSpacing: '.16em', textTransform: 'uppercase', color: suRayo, mb: .4
                    }}>
                      {nombreVoz(c.entidad, t)}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', color: 'var(--texto)', fontSize: '1.08rem', lineHeight: 1.3 }}>
                      {formatoTitulo(c.titulo)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Volver arriba */}
        <Box className="no-imprimir" sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <Box
            component="button" type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: .6, cursor: 'pointer',
              bgcolor: 'transparent', border: 0, color: 'var(--texto-tenue)',
              fontFamily: 'var(--fuente-titulo)', fontSize: '.74rem', letterSpacing: '.14em',
              textTransform: 'uppercase', '&:hover': { color: 'var(--texto)' }
            }}
          >
            <ArrowUpIcon sx={{ fontSize: '1rem' }} /> {t('ficha.volver_arriba')}
          </Box>
        </Box>

      </Container>

      <Snackbar
        open={!!aviso}
        autoHideDuration={3500}
        onClose={() => setAviso('')}
        message={aviso}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

const btnIcono = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  width: '2.4rem', height: '2.4rem', borderRadius: '2px',
  border: '1px solid var(--linea)', bgcolor: 'transparent', color: 'var(--texto-suave)',
  '&:hover': { color: 'var(--texto)' }
};

export default CanalizacionPage;
